import { FormEvent, useState } from 'react';
import { Building2, LocateFixed, MapPin } from 'lucide-react';
import { api } from '../../services/api';
import { Business, BusinessType, businessTypes } from '../../services/types';
import '../../styles/business-profile-panel.css';

type Tab = 'perfil' | 'ubicacion';

export function BusinessProfilePanel({ business, token, onSaved }: { business: Business; token: string; onSaved: (business: Business) => void }) {
  const [tab, setTab] = useState<Tab>('perfil');
  const [form, setForm] = useState({ name: business.name, phone: business.phone, businessType: business.businessType || ('otro' as BusinessType), branches: business.branches });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [location, setLocation] = useState({ latitude: business.location?.latitude ?? null, longitude: business.location?.longitude ?? null });
  const [locating, setLocating] = useState(false);
  const [locationSaving, setLocationSaving] = useState(false);
  const [locationMessage, setLocationMessage] = useState('');

  async function save(event: FormEvent) {
    event.preventDefault(); setSaving(true); setMessage('');
    try {
      const result = await api<{ business: Business }>('/business/profile', token, { method: 'PUT', body: JSON.stringify(form) });
      onSaved(result.business); setMessage('Datos del negocio guardados.');
    } catch (failure) { setMessage(failure instanceof Error ? failure.message : 'No pudimos guardar los datos del negocio.'); }
    finally { setSaving(false); }
  }

  function currentLocation() {
    setLocationMessage('');
    if (!navigator.geolocation) return setLocationMessage('Este dispositivo no permite obtener la ubicación.');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      setLocation({ latitude: Number(coords.latitude.toFixed(6)), longitude: Number(coords.longitude.toFixed(6)) });
      setLocating(false);
    }, () => { setLocationMessage('No pudimos obtenerla. Puedes escribir las coordenadas a mano.'); setLocating(false); }, { enableHighAccuracy: true, timeout: 12000 });
  }

  async function saveLocation(event: FormEvent) {
    event.preventDefault(); setLocationSaving(true); setLocationMessage('');
    try {
      const result = await api<{ business: Business }>('/business/location', token, { method: 'PUT', body: JSON.stringify(location) });
      onSaved(result.business); setLocationMessage('Ubicación guardada. Ya avisamos a las tarjetas de tus clientes, aunque ya las tuvieran instaladas.');
    } catch (failure) { setLocationMessage(failure instanceof Error ? failure.message : 'No pudimos guardar la ubicación.'); }
    finally { setLocationSaving(false); }
  }

  return <div className="owner-page business-settings">
    <div className="owner-heading"><span>MI NEGOCIO</span><h1>Negocio.</h1><p>Los datos que definen tu negocio y cómo Wallet le sugiere tu tarjeta a los clientes.</p></div>

    <div className="settings-tabs" role="tablist" aria-label="Configuración del negocio">
      <button type="button" role="tab" aria-selected={tab === 'perfil'} className={tab === 'perfil' ? 'active' : ''} onClick={() => setTab('perfil')}><Building2 /> Perfil</button>
      <button type="button" role="tab" aria-selected={tab === 'ubicacion'} className={tab === 'ubicacion' ? 'active' : ''} onClick={() => setTab('ubicacion')}><MapPin /> Ubicación</button>
    </div>

    {tab === 'perfil' && <form className="settings-card" onSubmit={save}>
      <div className="settings-row">
        <div><label htmlFor="biz-name">Nombre del negocio</label><small>Así aparece arriba de tu tarjeta y en el panel.</small></div>
        <input id="biz-name" required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Cafetería Central" />
      </div>
      <div className="settings-row">
        <div><label htmlFor="biz-type">Tipo de negocio</label><small>Así elegimos los sellos que mejor le quedan a tu tarjeta.</small></div>
        <select id="biz-type" value={form.businessType} onChange={(event) => setForm((current) => ({ ...current, businessType: event.target.value as BusinessType }))}>
          {businessTypes.map((type) => <option key={type.id} value={type.id}>{type.label}</option>)}
        </select>
      </div>
      <div className="settings-row">
        <div><label htmlFor="biz-phone">Teléfono del negocio</label><small>Opcional.</small></div>
        <input id="biz-phone" type="tel" value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} placeholder="55 1234 5678" />
      </div>
      <div className="settings-row">
        <div><label htmlFor="biz-branches">Sucursales</label><small>Cuántos locales operan bajo esta cuenta.</small></div>
        <input id="biz-branches" type="number" min="1" max="99" value={form.branches} onChange={(event) => setForm((current) => ({ ...current, branches: Number(event.target.value) }))} />
      </div>
      <footer>{message && <span className="settings-message">{message}</span>}<button disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'} <b>→</b></button></footer>
    </form>}

    {tab === 'ubicacion' && <form className="settings-card" onSubmit={saveLocation}>
      <p className="settings-intro">La usan Apple y Google Wallet para sugerirle tu tarjeta al cliente en la pantalla de bloqueo cuando está cerca de tu local — no es una dirección visible, solo el punto exacto en el mapa.</p>
      <div className="settings-row">
        <div><label>Detectar automáticamente</label><small>Usa el GPS de este dispositivo.</small></div>
        <button type="button" className="detect-location" onClick={currentLocation} disabled={locating}><LocateFixed /> {locating ? 'Obteniendo ubicación…' : 'Usar mi ubicación actual'}</button>
      </div>
      <div className="settings-row">
        <div><label htmlFor="biz-lat">Latitud</label></div>
        <input id="biz-lat" required type="number" step="any" min="-90" max="90" value={location.latitude ?? ''} onChange={(event) => setLocation((current) => ({ ...current, latitude: event.target.value === '' ? null : Number(event.target.value) }))} />
      </div>
      <div className="settings-row">
        <div><label htmlFor="biz-lng">Longitud</label></div>
        <input id="biz-lng" required type="number" step="any" min="-180" max="180" value={location.longitude ?? ''} onChange={(event) => setLocation((current) => ({ ...current, longitude: event.target.value === '' ? null : Number(event.target.value) }))} />
      </div>
      <footer>{locationMessage && <span className="settings-message">{locationMessage}</span>}<button disabled={locationSaving}>{locationSaving ? 'Guardando…' : 'Guardar ubicación'} <b>→</b></button></footer>
    </form>}
  </div>;
}
